import { useMemo } from 'react';
import { useWorkflow } from 'src/distribution_platforms/features/hooks/use_workflow';
import { SpecimenType } from 'src/types';
import { SpecimenTypeUtils } from 'src/utils';

export interface SpecimenTypeInfo {
  type: SpecimenType;
  displayName: string;
  description: string;
  enabled: boolean;
  order: number;
}

export interface UseOrderingSpecimenTypesResult {
  /** All available specimen types with their metadata and enabled status */
  specimenTypes: SpecimenTypeInfo[];
  /** Only enabled specimen types */
  enabledSpecimenTypes: SpecimenTypeInfo[];
  /** Check if a specific specimen type is enabled */
  isSpecimenTypeEnabled: (type: SpecimenType) => boolean;
  /** Check if multiple specimen types are enabled (for UI logic) */
  hasMultipleSpecimenTypes: boolean;
  /** Get metadata for a specific specimen type */
  getSpecimenTypeMetadata: (
    type: SpecimenType,
  ) => ReturnType<typeof SpecimenTypeUtils.getSpecimenTypeMetadata>;
}

/**
 * Hook to manage specimen type feature flags and metadata across all platforms
 *
 * This hook provides:
 * - Feature flag functionality based on platform + region
 * - Specimen type metadata (display names, descriptions)
 * - Utility methods for checking enabled types and multi-type scenarios
 *
 * @returns UseOrderingSpecimenTypesResult
 */
export const useOrderingSpecimenTypes = (): UseOrderingSpecimenTypesResult => {
  const workflow = useWorkflow();
  const specimenTypeData = useMemo(() => {
    const allSpecimenTypes = Object.values(SpecimenType).map(
      (type): SpecimenTypeInfo => {
        const metadata = SpecimenTypeUtils.getSpecimenTypeMetadata(type);
        const enabled = workflow.supportedSpecimenTypes.has(type);

        return {
          type,
          enabled,
          ...metadata,
        };
      },
    );

    const enabledTypes = allSpecimenTypes
      .filter((spec) => spec.enabled)
      .sort((a, b) => a.order - b.order);

    return {
      allTypes: allSpecimenTypes,
      enabledTypes,
      hasMultiple: enabledTypes.length > 1,
    };
  }, [workflow]);

  const getSpecimenTypeMetadata = useMemo(
    () => (type: SpecimenType) =>
      SpecimenTypeUtils.getSpecimenTypeMetadata(type),
    [],
  );

  return {
    specimenTypes: specimenTypeData.allTypes,
    enabledSpecimenTypes: specimenTypeData.enabledTypes,
    isSpecimenTypeEnabled: (type: SpecimenType) =>
      workflow.supportedSpecimenTypes.has(type),
    hasMultipleSpecimenTypes: specimenTypeData.hasMultiple,
    getSpecimenTypeMetadata,
  };
};
